import React, { useState, useEffect, useRef } from 'react';
import { useMap, useMapEvents, Marker, Polyline, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Navigation, X, Compass, MapPin, Target, Trash2, Edit3, Check, AlertTriangle, Clock, Anchor, Loader2, ShieldCheck, Volume2, VolumeX, Play, Square, Flag, Lock } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { navigationAPI } from '@/lib/api';
import { saveOffline, loadOffline, coordKey } from '@/lib/offlineStore';

// Custom destination marker icon
const destinationIcon = L.divIcon({
  className: 'custom-destination-marker',
  html: `
    <div style="
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
      border: 3px solid white;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        transform: rotate(45deg);
        color: white;
        font-size: 14px;
        font-weight: bold;
      ">⚑</div>
    </div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Calculate bearing between two points
const calculateBearing = (lat1, lon1, lat2, lon2) => {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  let bearing = (Math.atan2(y, x) * 180) / Math.PI;
  bearing = (bearing + 360) % 360;

  return Math.round(bearing);
};

// Calculate distance between two points (in nautical miles)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3440.065; // Earth's radius in nautical miles
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

// Convert bearing to compass direction
const bearingToCompass = (bearing) => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(bearing / 22.5) % 16;
  return directions[index];
};

// Cross-track distance (nm) from point P to the segment A->B (planar approx).
const crossTrackNm = (p, a, b) => {
  const midLat = ((a.lat + b.lat) / 2) * Math.PI / 180;
  const kx = 60 * Math.cos(midLat); // nm per degree lon
  const ky = 60;                    // nm per degree lat
  const bx = (b.lon - a.lon) * kx, by = (b.lat - a.lat) * ky;
  const px = (p.lon - a.lon) * kx, py = (p.lat - a.lat) * ky;
  const len2 = bx * bx + by * by || 1e-9;
  let t = (px * bx + py * by) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = px - bx * t, cy = py - by * t;
  return Math.sqrt(cx * cx + cy * cy);
};

// Navigation mode event handler component
const NavigationClickHandler = ({ onDestinationSet, isActive }) => {
  useMapEvents({
    click: (e) => {
      if (isActive) {
        onDestinationSet({ lat: e.latlng.lat, lon: e.latlng.lng });
      }
    },
  });
  return null;
};

const NavigationPanel = ({ myLocation, isActive, onToggle, destination, setDestination, onRequirePlus }) => {
  const { user, isPaid } = useApp();
  const [navInfo, setNavInfo] = useState(null);

  // Prevent clicks on the panel / modal (rendered inside the Leaflet map
  // container) from bubbling to the map and triggering a destination click.
  const stopMapClick = (el) => {
    if (el) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  };
  const [showCoordInput, setShowCoordInput] = useState(false);
  const [inputLat, setInputLat] = useState('');
  const [inputLon, setInputLon] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingDestination, setPendingDestination] = useState(null);
  const [shallowWarning, setShallowWarning] = useState(null);
  const [depthReport, setDepthReport] = useState(null);
  const [depthLoading, setDepthLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [guidanceActive, setGuidanceActive] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [currentLeg, setCurrentLeg] = useState(0);
  const [guidanceInfo, setGuidanceInfo] = useState(null);
  const spokenRef = useRef({ leg: -1, approach: -1 });
  const voiceOnRef = useRef(true);
  voiceOnRef.current = voiceOn;
  const myLocationRef = useRef(myLocation);
  myLocationRef.current = myLocation;
  const map = useMap();

  // User's boat draft (in feet) + safety margin
  const boatDraft = user?.boat_draft_ft || null;
  const safetyMargin = 2; // 2 feet extra cushion
  const minSafeDepth = boatDraft ? boatDraft + safetyMargin : null;

  // Calculate ETA based on current speed
  const calculateETA = (distanceNm, speedKnots) => {
    if (!speedKnots || speedKnots < 0.5) return null; // Too slow to calculate
    const hoursToDestination = distanceNm / speedKnots;
    const minutes = Math.round(hoursToDestination * 60);
    
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes} min`;
    
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    if (remainingMins === 0) return `${hours}h`;
    return `${hours}h ${remainingMins}m`;
  };

  // Update navigation info when location or destination changes
  useEffect(() => {
    if (myLocation && destination) {
      const bearing = calculateBearing(myLocation.lat, myLocation.lon, destination.lat, destination.lon);
      const distance = calculateDistance(myLocation.lat, myLocation.lon, destination.lat, destination.lon);
      const speed = myLocation.speed || 0;
      const eta = calculateETA(distance, speed);
      
      setNavInfo({
        bearing,
        compass: bearingToCompass(bearing),
        distance: distance.toFixed(2),
        distanceFeet: (distance * 6076.12).toFixed(0),
        eta,
        speed: speed.toFixed(1),
      });
    } else {
      setNavInfo(null);
    }
  }, [myLocation, destination]);

  // Depth-aware route check (Phase 1): sample NOAA charted depths along the
  // route and flag segments shallower than the boat's draft + safety margin.
  const runDepthCheck = async (dest) => {
    const here = myLocationRef.current;
    if (!here || !dest) return;
    setDepthLoading(true);
    try {
      const { data } = await navigationAPI.depthCheck({
        start_lat: here.lat,
        start_lon: here.lon,
        dest_lat: dest.lat,
        dest_lon: dest.lon,
        draft_ft: boatDraft || undefined,
        safety_margin_ft: safetyMargin,
      });
      setDepthReport(data);
    } catch (e) {
      setDepthReport({ status: 'error', note: 'Could not reach the depth service. Try again.' });
    } finally {
      setDepthLoading(false);
    }
  };

  // Run the check whenever a destination is set/changed.
  useEffect(() => {
    if (destination) {
      runDepthCheck(destination);
    } else {
      setDepthReport(null);
      setRouteData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);

  // Auto-route around shallows (Phase 2): A* over NOAA depth grid.
  const buildSafeRoute = async () => {
    const here = myLocationRef.current;
    if (!here || !destination) return;
    if (!isPaid) {
      onRequirePlus?.('Auto-Route (avoid shallows)');
      return;
    }
    setRouteLoading(true);
    setRouteData(null);
    const routeKey = coordKey('route', destination.lat, destination.lon);
    try {
      const { data } = await navigationAPI.route({
        start_lat: here.lat,
        start_lon: here.lon,
        dest_lat: destination.lat,
        dest_lon: destination.lon,
        draft_ft: boatDraft || undefined,
        safety_margin_ft: safetyMargin,
      });
      setRouteData(data);
      if (data?.status === 'ok') {
        saveOffline(routeKey, data);
      }
    } catch (e) {
      // Offline / server unreachable: reuse the last route saved for this destination
      const cached = await loadOffline(routeKey);
      if (cached && cached.data?.status === 'ok') {
        setRouteData({ ...cached.data, _cached: true });
      } else {
        setRouteData({ status: 'error', note: 'Could not build a route. You appear to be offline and no saved route was found for this destination.' });
      }
    } finally {
      setRouteLoading(false);
    }
  };

  // ---- Phase 3: live turn-by-turn guidance + voice (Web Speech API) ----
  const speak = (text) => {
    if (!voiceOnRef.current) return;
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0;
      synth.speak(u);
    } catch (e) { /* TTS unavailable */ }
  };

  const startGuidance = () => {
    if (routeData?.status !== 'ok' || !routeData.legs?.length) return;
    if (!isPaid) {
      onRequirePlus?.('Turn-by-turn Guidance');
      return;
    }
    setGuidanceActive(true);
    setCurrentLeg(0);
    spokenRef.current = { leg: 0, approach: -1 };
    const first = routeData.legs[0];
    speak(`Starting navigation. ${first.instruction}. ${first.distance_nm} nautical miles.`);
  };

  const stopGuidance = () => {
    setGuidanceActive(false);
    setGuidanceInfo(null);
    try { window.speechSynthesis?.cancel(); } catch (e) {}
  };

  // Advance leg-by-leg and drive voice as the boater moves.
  useEffect(() => {
    if (!guidanceActive || !myLocation || routeData?.status !== 'ok') return;
    const legs = routeData.legs || [];
    const idx = currentLeg;
    if (idx >= legs.length) return;
    const leg = legs[idx];
    const ARRIVE_NM = 0.08;
    const APPROACH_NM = 0.5;

    const distToWp = calculateDistance(myLocation.lat, myLocation.lon, leg.to.lat, leg.to.lon);

    // Reached this waypoint -> advance or finish.
    if (distToWp <= ARRIVE_NM) {
      const next = idx + 1;
      if (next >= legs.length) {
        speak('You have arrived at your destination.');
        setGuidanceInfo({ arrived: true });
        setGuidanceActive(false);
        return;
      }
      const nl = legs[next];
      speak(`${nl.instruction}. ${nl.distance_nm} nautical miles.`);
      spokenRef.current = { leg: next, approach: -1 };
      setCurrentLeg(next);
      return;
    }

    // Announce the start of a new leg once.
    if (spokenRef.current.leg !== idx) {
      speak(`${leg.instruction}. ${leg.distance_nm} nautical miles.`);
      spokenRef.current.leg = idx;
    }
    // Proximity callout for the upcoming maneuver, once.
    if (idx + 1 < legs.length && distToWp <= APPROACH_NM && spokenRef.current.approach !== idx) {
      speak(`In half a nautical mile, ${legs[idx + 1].instruction.toLowerCase()}.`);
      spokenRef.current.approach = idx;
    }

    const xte = crossTrackNm(myLocation, leg.from, leg.to);
    const remaining = distToWp + legs.slice(idx + 1).reduce((s, l) => s + l.distance_nm, 0);
    const speed = myLocation.speed || 0;
    let eta = null;
    if (speed >= 0.5) {
      const mins = Math.round((remaining / speed) * 60);
      eta = mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
    }
    setGuidanceInfo({
      instruction: leg.instruction,
      compass: leg.compass,
      nextInstruction: idx + 1 < legs.length ? legs[idx + 1].instruction : 'Arrive at destination',
      distToWp: distToWp.toFixed(2),
      remaining: remaining.toFixed(1),
      eta,
      offRoute: xte > 0.3,
      xte: xte.toFixed(2),
      bearingToWp: calculateBearing(myLocation.lat, myLocation.lon, leg.to.lat, leg.to.lon),
      legNum: idx + 1,
      totalLegs: legs.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myLocation, guidanceActive, currentLeg, routeData]);

  // Stop guidance if the destination is cleared.
  useEffect(() => {
    if (!destination) stopGuidance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);

  // Derived banner from the depth report.
  useEffect(() => {
    if (!depthReport || depthReport.status === 'error') {
      setShallowWarning(null);
      return;
    }
    const map = {
      danger: { severity: 'danger', message: `⚠ Shallow water on route — as low as ${depthReport.shallowest_ft ?? '?'} ft (your draft ${boatDraft ?? '?'} ft). Avoid or reroute.` },
      warning: { severity: 'warning', message: `Caution: charted depth dips to ${depthReport.shallowest_ft ?? '?'} ft, within your ${safetyMargin} ft safety margin.` },
      ok: { severity: 'ok', message: `Clear: shallowest charted depth ${depthReport.shallowest_ft ?? '?'} ft along route.` },
      no_draft: { severity: 'info', message: `Shallowest charted depth ${depthReport.shallowest_ft ?? '?'} ft. Set your boat draft in Settings for shallow-water alerts.` },
      no_data: { severity: 'info', message: 'No NOAA depth data for this route (outside US charted waters).' },
    };
    setShallowWarning(map[depthReport.status] || null);
  }, [depthReport, boatDraft, safetyMargin]);

  const handleDestinationSet = (dest) => {
    // Show disclaimer first, then set destination
    setPendingDestination(dest);
    setShowDisclaimer(true);
  };

  const acceptDisclaimer = () => {
    if (pendingDestination) {
      setDestination(pendingDestination);
      setPendingDestination(null);
    }
    setShowDisclaimer(false);
    setShowCoordInput(false);
  };

  const cancelDisclaimer = () => {
    setPendingDestination(null);
    setShowDisclaimer(false);
  };

  const clearDestination = () => {
    setDestination(null);
    setNavInfo(null);
  };

  const handleClose = () => {
    // Just close the panel, DON'T clear destination
    onToggle();
  };

  const handleCoordSubmit = () => {
    const lat = parseFloat(inputLat);
    const lon = parseFloat(inputLon);
    
    if (isNaN(lat) || isNaN(lon)) {
      return;
    }
    
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      return;
    }
    
    // Show disclaimer before setting destination
    setPendingDestination({ lat, lon });
    setShowDisclaimer(true);
  };
  
  const finishCoordSubmit = () => {
    setInputLat('');
    setInputLon('');
    
    // Center map on destination
    if (destination) {
      map.flyTo([destination.lat, destination.lon], 12);
    }
  };
  
  // After accepting disclaimer, fly to destination
  useEffect(() => {
    if (destination && !showDisclaimer && !pendingDestination) {
      finishCoordSubmit();
    }
  }, [destination]);

  // Always render markers/line if destination exists (even when panel closed)
  const renderMapElements = () => (
    <>
      {/* Click handler - only active when panel is open */}
      <NavigationClickHandler onDestinationSet={handleDestinationSet} isActive={isActive && !showCoordInput} />

      {/* Destination marker - always show if destination exists */}
      {destination && (
        <>
          <Marker position={[destination.lat, destination.lon]} icon={destinationIcon}>
            <Popup>
              <div className="text-sm">
                <p className="font-bold">Destination</p>
                <p>{destination.lat.toFixed(5)}, {destination.lon.toFixed(5)}</p>
                {navInfo && <p className="text-blue-600 font-medium">{navInfo.distance} nm away</p>}
              </div>
            </Popup>
          </Marker>

          {/* Line from current location to destination */}
          {myLocation && (
            <Polyline
              positions={[
                [myLocation.lat, myLocation.lon],
                [destination.lat, destination.lon],
              ]}
              pathOptions={{
                color: '#3b82f6',
                weight: 3,
                dashArray: '10, 10',
                opacity: 0.8,
              }}
            />
          )}

          {/* Shallow-water hazard markers along the route (NOAA depth check) */}
          {depthReport?.shallow_points?.map((sp, idx) => (            <CircleMarker
              key={`shallow-${idx}`}
              center={[sp.lat, sp.lon]}
              radius={7}
              pathOptions={{
                color: sp.level === 'danger' ? '#dc2626' : '#d97706',
                fillColor: sp.level === 'danger' ? '#ef4444' : '#f59e0b',
                fillOpacity: 0.7,
                weight: 2,
              }}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-bold" style={{ color: sp.level === 'danger' ? '#dc2626' : '#d97706' }}>
                    {sp.level === 'danger' ? '⚠ Shallow — aground risk' : 'Caution — shallow'}
                  </p>
                  <p>Charted depth: {sp.depth_ft} ft</p>
                  <p className="text-xs text-gray-500">{sp.lat.toFixed(4)}, {sp.lon.toFixed(4)}</p>
                </div>
              </Popup>
            </CircleMarker>
          ))}

          {/* Safe route (Phase 2 auto-routing around shallows) */}
          {routeData?.status === 'ok' && routeData.waypoints?.length > 1 && (
            <>
              <Polyline
                positions={routeData.waypoints.map((w) => [w.lat, w.lon])}
                pathOptions={{ color: '#16a34a', weight: 4, opacity: 0.9 }}
              />
              {guidanceActive && routeData.legs?.[currentLeg] && (
                <Polyline
                  positions={[
                    [routeData.legs[currentLeg].from.lat, routeData.legs[currentLeg].from.lon],
                    [routeData.legs[currentLeg].to.lat, routeData.legs[currentLeg].to.lon],
                  ]}
                  pathOptions={{ color: '#06b6d4', weight: 6, opacity: 0.95 }}
                />
              )}
              {routeData.waypoints.slice(1, -1).map((w, idx) => (
                <CircleMarker
                  key={`wp-${idx}`}
                  center={[w.lat, w.lon]}
                  radius={5}
                  pathOptions={{ color: '#15803d', fillColor: '#22c55e', fillOpacity: 0.9, weight: 2 }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold text-green-700">Waypoint {idx + 1}</p>
                      <p className="text-xs text-gray-500">{w.lat.toFixed(4)}, {w.lon.toFixed(4)}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </>
          )}
        </>
      )}
    </>
  );

  // If panel not active, still render map elements
  if (!isActive) {
    return renderMapElements();
  }

  return (
    <>
      {renderMapElements()}

      {/* Navigation info panel */}
      <Card ref={stopMapClick} className="absolute bottom-24 left-4 right-4 z-[1000] bg-white/95 backdrop-blur shadow-lg max-w-md mx-auto" data-testid="navigation-panel">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-blue-600" />
              <span className="font-semibold">Navigation</span>
            </div>
            <div className="flex gap-1">
              {destination && (
                <Button size="sm" variant="ghost" onClick={clearDestination} className="h-8 w-8 p-0" title="Clear destination">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={handleClose} className="h-8 w-8 p-0" data-testid="nav-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Live guidance card (Phase 3) */}
          {guidanceActive && guidanceInfo && !guidanceInfo.arrived && (
            <div className="mb-3 rounded-xl bg-slate-900 text-white p-3" data-testid="nav-guidance-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-300">Leg {guidanceInfo.legNum} of {guidanceInfo.totalLegs}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setVoiceOn((v) => !v)} className="h-7 w-7 p-0 text-white hover:bg-white/10" title="Toggle voice" data-testid="nav-voice-toggle">
                    {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-slate-400" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={stopGuidance} className="h-7 w-7 p-0 text-white hover:bg-white/10" title="Stop guidance" data-testid="nav-stop-guidance-btn">
                    <Square className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" style={{ transform: `rotate(${guidanceInfo.bearingToWp}deg)` }}>
                  <div className="text-white text-xl">↑</div>
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold leading-tight">{guidanceInfo.instruction}</p>
                  <p className="text-xs text-slate-300">in {guidanceInfo.distToWp} nm · then {guidanceInfo.nextInstruction}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-center">
                <div className="bg-white/10 rounded-lg py-1">
                  <p className="text-[10px] text-slate-300">To destination</p>
                  <p className="text-sm font-semibold">{guidanceInfo.remaining} nm</p>
                </div>
                <div className="bg-white/10 rounded-lg py-1">
                  <p className="text-[10px] text-slate-300">ETA</p>
                  <p className="text-sm font-semibold">{guidanceInfo.eta || '—'}</p>
                </div>
              </div>
              {guidanceInfo.offRoute && (
                <div className="mt-2 flex items-center gap-2 bg-amber-500/20 text-amber-300 rounded-lg p-2 text-xs" data-testid="nav-offroute-warning">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>Off route ({guidanceInfo.xte} nm). </span>
                  <button onClick={buildSafeRoute} className="underline font-medium">Reroute</button>
                </div>
              )}
            </div>
          )}

          {/* Arrival banner */}
          {guidanceInfo?.arrived && (
            <div className="mb-3 rounded-xl bg-green-600 text-white p-3 flex items-center gap-2" data-testid="nav-arrived-banner">
              <Flag className="w-5 h-5" />
              <span className="font-semibold">You have arrived at your destination.</span>
            </div>
          )}

          {/* Coordinate Input Mode */}
          {showCoordInput ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-500">Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. 25.7617"
                    value={inputLat}
                    onChange={(e) => setInputLat(e.target.value)}
                    className="h-9"
                    data-testid="nav-lat-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. -80.1918"
                    value={inputLon}
                    onChange={(e) => setInputLon(e.target.value)}
                    className="h-9"
                    data-testid="nav-lon-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={handleCoordSubmit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={!inputLat || !inputLon}
                  data-testid="nav-set-coords-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Set Destination
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowCoordInput(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : !destination ? (
            <div className="text-center py-3">
              <MapPin className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-3">Tap the map to set destination</p>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setShowCoordInput(true)}
                className="w-full"
                data-testid="nav-enter-coords-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Enter Lat/Lon Coordinates
              </Button>
              <p className="text-xs text-gray-400 mt-2">Uses NOAA charts for depth reference</p>
            </div>
          ) : navInfo ? (
            <div className="space-y-3">
              {/* Shallow water warning (live NOAA depth check) */}
              {depthLoading ? (
                <div className="flex items-center gap-2 p-2 rounded-lg text-xs bg-blue-50 text-blue-600" data-testid="depth-check-loading">
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                  <span>Checking charted depths along your route…</span>
                </div>
              ) : shallowWarning ? (
                <div
                  data-testid="depth-check-result"
                  data-depth-status={depthReport?.status}
                  className={`flex items-start gap-2 p-2 rounded-lg text-xs ${
                    shallowWarning.severity === 'danger' ? 'bg-red-100 text-red-700' :
                    shallowWarning.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                    shallowWarning.severity === 'ok' ? 'bg-green-50 text-green-700' :
                    'bg-blue-50 text-blue-600'
                  }`}
                >
                  {shallowWarning.severity === 'ok'
                    ? <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    : <Anchor className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <span>{shallowWarning.message}</span>
                </div>
              ) : null}

              {/* Compass arrow showing bearing direction */}
              <div className="flex justify-center">
                <div 
                  className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg"
                  style={{ transform: `rotate(${navInfo.bearing}deg)` }}
                >
                  <div className="text-white text-2xl">↑</div>
                </div>
              </div>
              <p className="text-xs text-center text-gray-500">
                Steer {navInfo.compass} ({navInfo.bearing}°)
              </p>

              {/* Bearing, Distance, ETA */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Compass className="w-3 h-3 text-blue-600" />
                    <span className="text-xs text-gray-500">Bearing</span>
                  </div>
                  <p className="text-lg font-bold text-blue-600">{navInfo.bearing}°</p>
                  <p className="text-xs text-gray-600">{navInfo.compass}</p>
                </div>
                <div className="text-center p-2 bg-cyan-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Target className="w-3 h-3 text-cyan-600" />
                    <span className="text-xs text-gray-500">Distance</span>
                  </div>
                  <p className="text-lg font-bold text-cyan-600">{navInfo.distance}</p>
                  <p className="text-xs text-gray-600">nm</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Clock className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-gray-500">ETA</span>
                  </div>
                  <p className="text-lg font-bold text-green-600">
                    {navInfo.eta || '--'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {navInfo.eta ? `@ ${navInfo.speed} kt` : 'stopped'}
                  </p>
                </div>
              </div>

              {/* Destination coords */}
              <div className="text-xs text-gray-500 text-center bg-gray-50 rounded p-2">
                Destination: {destination.lat.toFixed(5)}, {destination.lon.toFixed(5)}
              </div>

              {/* Edit coordinates button */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setInputLat(destination.lat.toString());
                  setInputLon(destination.lon.toString());
                  setShowCoordInput(true);
                }}
                className="w-full"
                data-testid="nav-edit-coords-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Coordinates
              </Button>

              {/* Re-check depths */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => runDepthCheck(destination)}
                disabled={depthLoading}
                className="w-full"
                data-testid="nav-recheck-depth-btn"
              >
                {depthLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Anchor className="w-4 h-4 mr-2" />}
                Re-check Depths
              </Button>

              {/* Auto-route around shallows (Phase 2) */}
              <Button
                size="sm"
                onClick={buildSafeRoute}
                disabled={routeLoading}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="nav-autoroute-btn"
              >
                {routeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (!isPaid ? <Lock className="w-4 h-4 mr-2" /> : <Navigation className="w-4 h-4 mr-2" />)}
                {routeData?.status === 'ok' ? 'Rebuild Safe Route' : 'Auto-Route (avoid shallows)'}
                {!isPaid && <span className="ml-1 text-[10px] bg-white/25 rounded px-1 py-0.5">PLUS</span>}
              </Button>

              {/* Route result / turn-by-turn */}
              {routeData && (
                <div data-testid="autoroute-result" data-route-status={routeData.status}>
                  {routeData.status === 'ok' ? (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-green-700 flex items-center gap-1">
                          <Navigation className="w-3 h-3" /> Safe route
                        </span>
                        <span className="text-xs text-green-700">
                          {routeData.total_distance_nm} nm · {routeData.legs?.length || 0} legs
                        </span>
                      </div>
                      <ol className="max-h-28 overflow-y-auto space-y-0.5">
                        {routeData.legs?.map((leg, i) => (
                          <li key={i} className="text-xs text-gray-600 flex gap-2">
                            <span className="text-green-600 font-medium">{i + 1}.</span>
                            <span>{leg.instruction} — {leg.distance_nm} nm ({leg.compass})</span>
                          </li>
                        ))}
                      </ol>
                      {routeData.assumed_draft && (
                        <p className="text-xs text-amber-600 mt-1">Assumed 3 ft draft — set your draft for accuracy.</p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-amber-50 text-amber-700 p-2 text-xs flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        {routeData.status === 'no_route' && (routeData.reason || 'No clear deep-water path found.')}
                        {routeData.status === 'no_data' && 'No NOAA depth data for this area (outside US charted waters).'}
                        {routeData.status === 'error' && routeData.note}
                        {' '}Auto-routing is an advisory pilot (US, Great Lakes) and can’t detour around large landmasses.
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Start live guidance (Phase 3) */}
              {routeData?.status === 'ok' && !guidanceActive && (
                <Button
                  size="sm"
                  onClick={startGuidance}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="nav-start-guidance-btn"
                >
                  {!isPaid ? <Lock className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  Start Guidance
                  {!isPaid && <span className="ml-1 text-[10px] bg-white/25 rounded px-1 py-0.5">PLUS</span>}
                </Button>
              )}

              {depthReport?.data_coverage === 'partial' && (
                <p className="text-xs text-blue-600 text-center">
                  Partial NOAA coverage — some points along this route are uncharted.
                </p>
              )}

              {/* Draft info */}
              {!boatDraft && (
                <p className="text-xs text-amber-600 text-center">
                  <Anchor className="w-3 h-3 inline mr-1" />
                  Set boat draft in Settings for shallow water alerts
                </p>
              )}

              {/* Disclaimer */}
              <p className="text-xs text-gray-400 text-center">
                ⚠️ For reference only. Captain is responsible for safe navigation.
              </p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-600">Enable location to see navigation info</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div ref={stopMapClick} className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-2" data-testid="nav-disclaimer-modal">
          <Card className="w-full max-w-md bg-white shadow-xl max-h-[90vh] flex flex-col">
            <CardContent className="p-4 flex flex-col h-full">
              {/* Header - fixed */}
              <div className="flex items-center gap-3 mb-3 flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-800">Navigation Disclaimer</h2>
              </div>
              
              {/* Scrollable content */}
              <div className="overflow-y-auto flex-1 mb-3">
                <div className="space-y-2 text-sm text-gray-600">
                  <p className="font-medium text-gray-800">
                    This is for <span className="text-red-600">REFERENCE ONLY</span>.
                  </p>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    <li>Does <strong>NOT</strong> provide turn-by-turn navigation</li>
                    <li>Shows bearing and distance only</li>
                    <li>Does <strong>NOT</strong> account for depth, obstacles, or hazards</li>
                    <li>Always use official nautical charts</li>
                    <li><strong>Captain is solely responsible</strong> for safe navigation</li>
                  </ul>
                  <p className="bg-yellow-50 border border-yellow-200 rounded p-2 text-yellow-800 text-xs">
                    ⚠️ Do NOT rely on this app for navigation. Maintain proper lookout and follow maritime rules.
                  </p>
                </div>
              </div>
              
              {/* Buttons - fixed at bottom */}
              <div className="flex gap-2 flex-shrink-0">
                <Button 
                  variant="outline" 
                  className="flex-1 h-12 text-base"
                  onClick={cancelDisclaimer}
                  data-testid="nav-disclaimer-cancel"
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 h-12 text-base bg-blue-600 hover:bg-blue-700"
                  onClick={acceptDisclaimer}
                  data-testid="nav-disclaimer-accept"
                >
                  I Understand
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default NavigationPanel;
